import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type Task, type TaskListItem, taskKeys } from '@/entities/task'
import { apiRequest } from '@/shared/api'

export type EditTaskVars = {
  title?: string
  due?: string
  // Снятие срока — через clear_due (null в PATCH не проходит схему бэка).
  clear_due?: boolean
  // Блокирует перенос проекта на следующий этап; бэк игнорирует у заказчика.
  blocking?: boolean
}

export function useEditTask(taskId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: EditTaskVars) =>
      apiRequest({ method: 'PATCH', url: `/tasks/${taskId}`, body: vars }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.byProject(projectId) })
      void qc.invalidateQueries({ queryKey: taskKeys.lists })
      void qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) })
    },
  })
}

// Отметка выполнения — оптимистично (мгновенный отклик чекбокса).
export function useCompleteTask(projectId: string) {
  const qc = useQueryClient()
  const key = taskKeys.byProject(projectId)
  return useMutation({
    mutationFn: (vars: { id: string; done: boolean }) =>
      apiRequest({ method: 'POST', url: `/tasks/${vars.id}/${vars.done ? 'complete' : 'reopen'}` }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<Task[]>(key)
      qc.setQueryData<Task[]>(key, (old) =>
        old?.map((t) => (t.id === vars.id ? { ...t, done: vars.done, overdue: false } : t)),
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: key })
      void qc.invalidateQueries({ queryKey: taskKeys.lists })
      void qc.invalidateQueries({ queryKey: taskKeys.detail(vars.id) })
    },
  })
}

// Выполнение из глобального списка задач — оптимистично: выполненные скрыты
// (open_only), поэтому закрытая задача уходит из списка сразу.
export function useCompleteListedTask() {
  const qc = useQueryClient()
  const key = taskKeys.lists
  return useMutation({
    mutationFn: (vars: { id: string; done: boolean }) =>
      apiRequest({ method: 'POST', url: `/tasks/${vars.id}/${vars.done ? 'complete' : 'reopen'}` }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<TaskListItem[]>(key)
      qc.setQueryData<TaskListItem[]>(key, (old) =>
        old?.filter((t) => !(vars.done && t.id === vars.id)),
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.lists })
    },
  })
}

export function useDeleteTask(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) => apiRequest({ method: 'DELETE', url: `/tasks/${taskId}` }),
    onSuccess: (_data, taskId) => {
      void qc.invalidateQueries({ queryKey: taskKeys.byProject(projectId) })
      void qc.invalidateQueries({ queryKey: taskKeys.lists })
      void qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) })
    },
  })
}
